/**
 * Why a web or mobile test failed (TestResult.failure). The runner reads the failure code e2e puts in
 * its report.json (a closed set assigned by e2e's runner, never chosen by the model) and maps it here;
 * 1QA's own test data checks before a run map to INVALID_TEST_DATA. See docs/architecture.md "Failure reasons".
 */

export const FAILURE_REASONS = ['ELEMENT_NOT_FOUND', 'ACTION_NOT_PERFORMED', 'INVALID_TEST_DATA', 'ENVIRONMENT'] as const;
export type FailureReason = (typeof FAILURE_REASONS)[number];

export const FAILURE_REASON_META: Record<FailureReason, { label: string; description: string; hint: string }> = {
  ELEMENT_NOT_FOUND: {
    label: 'Element not on screen',
    description: 'What the step expected - a button, field, text or state - was not on the screen.',
    hint: 'Check that the app shows it at this point, or that the step names it the way the screen does.',
  },
  ACTION_NOT_PERFORMED: {
    label: 'Action not performed',
    description: 'The element was on the screen, but the step could not complete the action on it.',
    hint: 'The element may be disabled, covered, ambiguous or slow to react. Make the step more specific, or add a wait.',
  },
  INVALID_TEST_DATA: {
    label: 'Invalid test data',
    description: 'The test data the step used was missing, rejected or not valid.',
    hint: 'Check the values on the Test data page for this collection, e.g. credentials or required records.',
  },
  ENVIRONMENT: {
    label: 'Environment problem',
    description: 'Not the test: the app, the device or browser, or the AI model was not available.',
    hint: 'Check that the app is reachable and the AI model is connected, then run the test again.',
  },
};

/** Stored on TestResult.failure for a FAILED or ERROR result (null when it passed or could not be classified). */
export interface FailureInfo {
  reason: FailureReason;
  /** e2e's failure code (e.g. LOCATOR_NOT_FOUND), or 1QA's (MISSING_TEST_DATA, SECRET_TOO_SHORT). */
  code?: string;
  /** 0-based index of the step that failed, when known. */
  stepIndex?: number;
  /** Short explanation from e2e (bounded, secrets masked by e2e). */
  message?: string;
  /** Structured facts from e2e: what an assertion expected and saw, what a locator looked for, how long it waited. */
  expected?: string;
  observed?: string;
  locator?: string;
  waitedMs?: number;
}

/** 1QA's own codes, raised before e2e runs. */
export const MISSING_TEST_DATA = 'MISSING_TEST_DATA';
export const SECRET_TOO_SHORT = 'SECRET_TOO_SHORT';

const REASON_BY_CODE: Record<string, FailureReason> = {
  // 1. Expected element not on the screen
  LOCATOR_NOT_FOUND: 'ELEMENT_NOT_FOUND',
  ASSERTION_FAILED: 'ELEMENT_NOT_FOUND',
  APP_NOT_OPEN: 'ELEMENT_NOT_FOUND',
  NODE_STALE: 'ELEMENT_NOT_FOUND',
  FRAME_NOT_FOUND: 'ELEMENT_NOT_FOUND',
  // 2. Element on the screen, action not performed
  ACTION_FAILED: 'ACTION_NOT_PERFORMED',
  LOCATOR_AMBIGUOUS: 'ACTION_NOT_PERFORMED',
  AUTOMATION_UNSUPPORTED: 'ACTION_NOT_PERFORMED',
  STEP_TIMEOUT: 'ACTION_NOT_PERFORMED',
  STEP_BUDGET_EXHAUSTED: 'ACTION_NOT_PERFORMED',
  STEP_NO_CONCLUSION: 'ACTION_NOT_PERFORMED',
  ASSERTION_INCONCLUSIVE: 'ACTION_NOT_PERFORMED',
  MODEL_OUTPUT_INVALID: 'ACTION_NOT_PERFORMED',
  CONTEXT_OVERFLOW: 'ACTION_NOT_PERFORMED',
  REPLAY_STALE: 'ACTION_NOT_PERFORMED',
  NOT_ACTIONABLE: 'ACTION_NOT_PERFORMED',
  ACTION_MAY_HAVE_COMMITTED: 'ACTION_NOT_PERFORMED',
  INVALID_STATE: 'ACTION_NOT_PERFORMED',
  FRAME_AMBIGUOUS: 'ACTION_NOT_PERFORMED',
  OPERATION_TIMEOUT: 'ACTION_NOT_PERFORMED',
  UNSUPPORTED_CAPABILITY: 'ACTION_NOT_PERFORMED',
  TEST_TIMEOUT: 'ACTION_NOT_PERFORMED',
  // 3. Invalid test data
  AUTHENTICATION_FAILED: 'INVALID_TEST_DATA',
  AUTH_CREDENTIAL_INVALID: 'INVALID_TEST_DATA',
  AUTH_CREDENTIAL_UNAVAILABLE: 'INVALID_TEST_DATA',
  SECRET_UNAVAILABLE: 'INVALID_TEST_DATA',
  SEED_DATA_MISSING: 'INVALID_TEST_DATA',
  [MISSING_TEST_DATA]: 'INVALID_TEST_DATA',
  [SECRET_TOO_SHORT]: 'INVALID_TEST_DATA',
  // Not the test
  APP_UNREACHABLE: 'ENVIRONMENT',
  APP_ALREADY_RUNNING: 'ENVIRONMENT',
  ENVIRONMENT_UNAVAILABLE: 'ENVIRONMENT',
  MODEL_UNAVAILABLE: 'ENVIRONMENT',
  MODEL_PROVIDER_FAILED: 'ENVIRONMENT',
  TEST_SETUP_FAILED: 'ENVIRONMENT',
  POLICY_DENIED: 'ENVIRONMENT',
  ENGINE_FAILURE: 'ENVIRONMENT',
  CANCELLED: 'ENVIRONMENT',
};

/**
 * The reason for an e2e failure code. Codes e2e may add later fall back on their category: anything
 * outside the test itself is ENVIRONMENT; an unknown `test` failure stays unclassified (null) rather than guessed.
 */
export function failureReasonForCode(code: string | undefined, category?: string): FailureReason | null {
  if (code && REASON_BY_CODE[code]) return REASON_BY_CODE[code];
  if (category === 'infrastructure' || category === 'internal' || category === 'interrupted') return 'ENVIRONMENT';
  return null;
}

export const isFailureReason = (v: unknown): v is FailureReason => typeof v === 'string' && (FAILURE_REASONS as readonly string[]).includes(v);
